/*
 * IMPORTANTE: Para resolver o problema de RLS com useful_links, 
 * execute o seguinte SQL no Supabase SQL Editor:
 * 
 * CREATE OR REPLACE FUNCTION delete_useful_link(link_id UUID)
 * RETURNS JSON
 * LANGUAGE plpgsql
 * SECURITY DEFINER
 * AS $$
 * DECLARE
 *   user_id UUID;
 *   user_role TEXT;
 *   result JSON;
 * BEGIN
 *   -- Obter o ID do usuário atual
 *   user_id := auth.uid();
 *   
 *   -- Verificar se o usuário está autenticado
 *   IF user_id IS NULL THEN
 *     RETURN json_build_object('success', false, 'message', 'Usuário não autenticado');
 *   END IF;
 *   
 *   -- Obter o role do usuário
 *   SELECT role INTO user_role
 *   FROM user_profiles
 *   WHERE id = user_id;
 *   
 *   -- Verificar se o usuário é admin
 *   IF user_role != 'admin' THEN
 *     RETURN json_build_object('success', false, 'message', 'Apenas administradores podem deletar links');
 *   END IF;
 *   
 *   -- Verificar se o link existe
 *   IF NOT EXISTS (SELECT 1 FROM useful_links WHERE id = link_id) THEN
 *     RETURN json_build_object('success', false, 'message', 'Link não encontrado');
 *   END IF;
 *   
 *   -- Executar soft delete
 *   UPDATE useful_links 
 *   SET is_active = false, updated_at = NOW()
 *   WHERE id = link_id;
 *   
 *   RETURN json_build_object('success', true, 'message', 'Link deletado com sucesso');
 * END;
 * $$;
 * 
 * -- Dar permissão para executar a função
 * GRANT EXECUTE ON FUNCTION delete_useful_link(UUID) TO authenticated;
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://meqeeisqsfqtplyqqxhv.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1lcWVlaXNxc2ZxdHBseXFxeGh2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQwNzYyNzksImV4cCI6MjA2OTY1MjI3OX0.uCnKKKyuGdGkamjPsXIZiU46cwzC_DMQziUQoY35X5M';

// Verificar se já existe uma instância do Supabase para evitar múltiplas instâncias
let supabaseInstance: ReturnType<typeof createClient> | null = null;

export const supabase = (() => {
  if (!supabaseInstance) {
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        storageKey: 'sb-meqeeisqsfqtplyqqxhv-auth-token',
        storage: typeof window !== 'undefined' ? localStorage : undefined,
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });
    
    // Log apenas em desenvolvimento para debug
    if (process.env.NODE_ENV === 'development') {
      console.log('🔧 Supabase client initialized');
    }
  }
  return supabaseInstance;
})();

// Tipos para o banco de dados
export interface UserProfile {
  id: string;
  email: string;
  full_name?: string;
  bio?: string;
  website?: string;
  location?: string;
  avatar_url?: string;
  role: 'user' | 'admin' | 'fornecedor' | 'mentor';
  is_supplier_requested?: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  event_type: 'call' | 'workshop' | 'meeting';
  event_date: string;
  duration_minutes: number;
  participants_limit?: number;
  location?: string;
  created_by?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'suspension' | 'reactivation' | 'info' | 'warning' | 'like';
  is_read: boolean;
  created_at: string;
}

// Funções de autenticação
export const authHelpers = {
  // Login com email e senha
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  signUp: async (email: string, password: string, fullName?: string, isSupplierRequested?: boolean) => {
    const redirectUrl = typeof window !== 'undefined' 
      ? `${window.location.origin}` 
      : undefined;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          is_supplier_requested: isSupplierRequested || false
        },
      },
    });
    return { data, error };
  },

  // Logout
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Obter sessão atual
  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  },

  // Obter perfil do usuário atual
  getCurrentUserProfile: async (): Promise<{ data: UserProfile | null; error: any }> => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Erro ao obter usuário:', userError);
        return { data: null, error: userError };
      }
      
      if (!user) {
        return { data: null, error: { message: 'Usuário não autenticado' } };
      }

      console.log('Buscando perfil para usuário:', user.id);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Erro ao buscar perfil:', error);
      } else if (data) {
        console.log('Perfil encontrado:', data.email);
      }

      return { data: data as UserProfile | null, error };
    } catch (error) {
      console.error('Erro inesperado ao obter perfil:', error);
      return { data: null, error };
    }
  },

  // Verificar se usuário é admin
  isAdmin: async (): Promise<boolean> => {
    try {
      const { data } = await supabase.rpc('is_admin');
      return Boolean(data);
    } catch (error) {
      console.error('Erro ao verificar role de admin:', error);
      return false;
    }
  },

  // Atualizar perfil do usuário
  updateProfile: async (updates: Partial<UserProfile>) => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      return { data: null, error: { message: 'Usuário não autenticado' } };
    }

    const { data, error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    return { data, error };
  },
};

// Funções para eventos do calendário
export const calendarHelpers = {
  // Obter todos os eventos ativos
  getActiveEvents: async (): Promise<{ data: CalendarEvent[] | null; error: any }> => {
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .eq('is_active', true)
      .order('event_date', { ascending: true });

    return { data: data as CalendarEvent[] | null, error };
  },

  // Criar novo evento (apenas admins)
  createEvent: async (event: Omit<CalendarEvent, 'id' | 'created_at' | 'updated_at' | 'created_by'>) => {
    const { data, error } = await supabase
      .from('calendar_events')
      .insert([event])
      .select()
      .single();

    return { data, error };
  },

  // Atualizar evento (apenas admins)
  updateEvent: async (id: string, updates: Partial<CalendarEvent>) => {
    const { data, error } = await supabase
      .from('calendar_events')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    return { data, error };
  },

  // Deletar evento (apenas admins)
  deleteEvent: async (id: string) => {
    const { data, error } = await supabase
      .from('calendar_events')
      .update({ is_active: false })
      .eq('id', id)
      .select()
      .single();

    return { data, error };
  },
};

// Funções para gerenciamento de usuários (apenas admins)
export const userManagementHelpers = {
  // Listar todos os usuários (apenas admins)
  getAllUsers: async (): Promise<{ data: UserProfile[] | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar usuários:', error);
        return { data: null, error };
      }

      return { data: data as unknown as UserProfile[], error: null };
    } catch (error) {
      console.error('Erro inesperado ao buscar usuários:', error);
      return { data: null, error };
    }
  },

  // Promover usuário a admin
  promoteToAdmin: async (userEmail: string): Promise<{ success: boolean; message: string; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc('promote_to_admin', {
        user_email: userEmail
      });

      console.log('📊 Resposta do RPC promote_to_admin:', { data, error });

      if (error) {
        console.error('❌ Erro ao promover usuário:', error);
        return { success: false, message: 'Erro ao promover usuário', error: error.message };
      }

      // Type assertion para a resposta do RPC
      const response = data as { success?: boolean; message?: string; error?: string };
      return { 
        success: Boolean(response?.success), 
        message: response?.message || response?.error || 'Operação concluída' 
      };
    } catch (error) {
      console.error('❌ Erro inesperado ao promover usuário:', error);
      return { success: false, message: 'Erro inesperado ao promover usuário' };
    }
  },

  // Rebaixar admin a usuário
  demoteAdmin: async (userEmail: string): Promise<{ success: boolean; message: string; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc('demote_admin', {
        user_email: userEmail
      });

      if (error) {
        console.error('Erro ao rebaixar admin:', error);
        return { success: false, message: 'Erro ao rebaixar admin', error: error.message };
      }

      // Type assertion para a resposta do RPC
      const response = data as { success?: boolean; message?: string; error?: string };
      return { 
        success: Boolean(response?.success), 
        message: response?.message || response?.error || 'Operação concluída' 
      };
    } catch (error) {
      console.error('Erro inesperado ao rebaixar admin:', error);
      return { success: false, message: 'Erro inesperado ao rebaixar admin' };
    }
  },

  // Suspender/reativar usuário
  toggleUserStatus: async (userEmail: string): Promise<{ success: boolean; message: string; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc('toggle_user_status', {
        user_email: userEmail
      });

      if (error) {
        console.error('Erro ao alterar status do usuário:', error);
        return { success: false, message: 'Erro ao alterar status do usuário', error: error.message };
      }

      // Type assertion para a resposta do RPC
      const response = data as { success?: boolean; message?: string; error?: string };
      return { 
        success: Boolean(response?.success), 
        message: response?.message || response?.error || 'Operação concluída' 
      };
    } catch (error) {
      console.error('Erro inesperado ao alterar status do usuário:', error);
      return { success: false, message: 'Erro inesperado ao alterar status do usuário' };
    }
  },

  // Buscar usuário por email
  getUserByEmail: async (email: string): Promise<{ data: UserProfile | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', email)
        .single();

      if (error) {
        console.error('Erro ao buscar usuário por email:', error);
        return { data: null, error };
      }

      return { data: data as unknown as UserProfile, error: null };
    } catch (error) {
      console.error('Erro inesperado ao buscar usuário por email:', error);
      return { data: null, error };
    }
  },

  // Aprovar fornecedor
  approveSupplier: async (userEmail: string): Promise<{ success: boolean; message: string; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc('approve_supplier', {
        user_email: userEmail
      });

      if (error) {
        console.error('Erro ao aprovar fornecedor:', error);
        return { success: false, message: 'Erro ao aprovar fornecedor', error: error.message };
      }

      const response = data as { success?: boolean; message?: string; error?: string };
      return { 
        success: Boolean(response?.success), 
        message: response?.message || response?.error || 'Operação concluída' 
      };
    } catch (error) {
      console.error('Erro inesperado ao aprovar fornecedor:', error);
      return { success: false, message: 'Erro inesperado ao aprovar fornecedor' };
    }
  },

  // Rebaixar fornecedor
  demoteSupplier: async (userEmail: string): Promise<{ success: boolean; message: string; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc('demote_supplier', {
        user_email: userEmail
      });

      if (error) {
        console.error('Erro ao rebaixar fornecedor:', error);
        return { success: false, message: 'Erro ao rebaixar fornecedor', error: error.message };
      }

      const response = data as { success?: boolean; message?: string; error?: string };
      return { 
        success: Boolean(response?.success), 
        message: response?.message || response?.error || 'Operação concluída' 
      };
    } catch (error) {
      console.error('Erro inesperado ao rebaixar fornecedor:', error);
      return { success: false, message: 'Erro inesperado ao rebaixar fornecedor' };
    }
  },

  // Promover usuário a mentor
  promoteToMentor: async (userEmail: string): Promise<{ success: boolean; message: string; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc('promote_to_mentor', {
        user_email: userEmail
      });

      if (error) {
        console.error('Erro ao promover a mentor:', error);
        return { success: false, message: 'Erro ao promover a mentor', error: error.message };
      }

      const response = data as { success?: boolean; message?: string; error?: string };
      return { 
        success: Boolean(response?.success), 
        message: response?.message || response?.error || 'Operação concluída' 
      };
    } catch (error) {
      console.error('Erro inesperado ao promover a mentor:', error);
      return { success: false, message: 'Erro inesperado ao promover a mentor' };
    }
  },

  // Rebaixar mentor a usuário comum
  demoteMentor: async (userEmail: string): Promise<{ success: boolean; message: string; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc('demote_mentor', {
        user_email: userEmail
      });

      if (error) {
        console.error('Erro ao rebaixar mentor:', error);
        return { success: false, message: 'Erro ao rebaixar mentor', error: error.message };
      }

      const response = data as { success?: boolean; message?: string; error?: string };
      return { 
        success: Boolean(response?.success), 
        message: response?.message || response?.error || 'Operação concluída' 
      };
    } catch (error) {
      console.error('Erro inesperado ao rebaixar mentor:', error);
      return { success: false, message: 'Erro inesperado ao rebaixar mentor' };
    }
  }
};

// Funções de notificação
export const notificationHelpers = {
  // Buscar notificações do usuário
  getUserNotifications: async (): Promise<{ data: Notification[] | null; error: any }> => {
    try {
      const { data, error } = await supabase.rpc('get_user_notifications');

      if (error) {
        console.error('Erro ao buscar notificações:', error);
        return { data: null, error };
      }

      return { data: data as Notification[], error: null };
    } catch (error) {
      console.error('Erro inesperado ao buscar notificações:', error);
      return { data: null, error };
    }
  },

  // Marcar notificação como lida
  markAsRead: async (notificationId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const { data, error } = await supabase.rpc('mark_notification_as_read', {
        notification_id: notificationId
      });

      if (error) {
        console.error('Erro ao marcar notificação como lida:', error);
        return { success: false, message: 'Erro ao marcar notificação como lida' };
      }

      // Type guard para verificar se data tem as propriedades esperadas
      if (data && typeof data === 'object' && 'success' in data) {
        return { 
          success: Boolean(data.success), 
          message: (data.message as string) || 'Notificação marcada como lida' 
        };
      }

      return { success: false, message: 'Resposta inválida do servidor' };
    } catch (error) {
      console.error('Erro inesperado ao marcar notificação como lida:', error);
      return { success: false, message: 'Erro inesperado ao marcar notificação como lida' };
    }
  },

  // Marcar todas as notificações como lidas
  markAllAsRead: async (): Promise<{ success: boolean; message: string }> => {
    try {
      const { data, error } = await supabase.rpc('mark_all_notifications_as_read');

      if (error) {
        console.error('Erro ao marcar todas as notificações como lidas:', error);
        return { success: false, message: 'Erro ao marcar notificações como lidas' };
      }

      // Type guard para verificar se data tem as propriedades esperadas
      if (data && typeof data === 'object' && 'success' in data) {
        return { 
          success: Boolean(data.success), 
          message: (data.message as string) || 'Todas as notificações marcadas como lidas' 
        };
      }

      return { success: false, message: 'Resposta inválida do servidor' };
    } catch (error) {
      console.error('Erro inesperado ao marcar notificações como lidas:', error);
      return { success: false, message: 'Erro inesperado ao marcar notificações como lidas' };
    }
  },

  // Contar notificações não lidas
  getUnreadCount: async (): Promise<{ count: number; error: any }> => {
    try {
      const { data, error } = await supabase.rpc('get_unread_notifications_count');

      if (error) {
        console.error('Erro ao contar notificações não lidas:', error);
        return { count: 0, error };
      }

      return { count: data || 0, error: null };
    } catch (error) {
      console.error('Erro inesperado ao contar notificações não lidas:', error);
      return { count: 0, error };
    }
  },


};

// Interfaces para o fórum
export interface ForumCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  banner_url?: string;
  banner_image?: string;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
  custom_tag?: string; // Nova etiqueta de texto livre
}

export interface ForumTag {
  id: string;
  name: string;
  color: string;
  is_active: boolean;
  created_by?: string;
  created_at: string;
}

export interface ForumTopic {
  id: string;
  title: string;
  content: string;
  category_id: string;
  created_by: string;
  is_pinned: boolean;
  is_locked: boolean;
  view_count: number;
  reply_count: number;
  votes_count: number; // Adicionando campo para contador de likes
  created_at: string;
  updated_at: string;
  // Campos relacionados
  category?: ForumCategory;
  author?: UserProfile;
  vote_stats?: { likes: number };
}

export interface ForumReply {
  id: string;
  topic_id: string;
  content: string;
  created_by: string;
  parent_reply_id?: string;
  is_solution: boolean;
  votes_count: number; // Adicionando campo para contador de likes
  created_at: string;
  updated_at: string;
  // Campos relacionados
  author?: UserProfile;
  vote_stats?: { likes: number };
}

export interface ForumVote {
  id: string;
  user_id: string;
  topic_id?: string;
  reply_id?: string;
  vote_type: 'like';
  created_at: string;
}

export interface UsefulLink {
  id: string;
  title: string;
  description: string;
  url: string;
  image: string;
  created_by: string;
  is_active: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface Mentor {
  id: string;
  name: string;
  title: string;
  bio: string;
  specialties: string[];
  experience_years: number;
  avatar_url?: string;
  banner_url?: string;
  banner_vertical_url?: string;
  contact_email?: string;
  linkedin_url?: string;
  github_url?: string;
  instagram_url?: string;
  website_url?: string;
  checkout_url?: string;
  whatsapp?: string;
  hourly_rate?: number;
  session_types: string[];
  languages: string[];
  availability: string;
  created_by: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  free_content?: {
    title: string;
    videos: { title: string; url: string }[];
  };
  slug?: string;
  user_id?: string | null;
  area_config?: any;
  access_duration_days?: number;
}

// Funções para o fórum
export const forumHelpers = {
  // Funções para tópicos salvos
  savedTopics: {
    save: async (topicId: string): Promise<{ success: boolean; message: string }> => {
      const { data, error } = await supabase.rpc('toggle_saved_topic', { topic_uuid: topicId });
      if (error) {
        console.error('Erro ao salvar tópico:', error);
        return { success: false, message: 'Erro ao salvar tópico' };
      }
      return data as { success: boolean; message: string };
    },

    unsave: async (topicId: string): Promise<{ success: boolean; message: string }> => {
      const { data, error } = await supabase.rpc('toggle_saved_topic', { topic_uuid: topicId });
      if (error) {
        console.error('Erro ao remover tópico dos salvos:', error);
        return { success: false, message: 'Erro ao remover tópico dos salvos' };
      }
      return data as { success: boolean; message: string };
    },

    isSaved: async (topicId: string): Promise<boolean> => {
      const { data, error } = await supabase.rpc('is_topic_saved_optimized', { topic_uuid: topicId });
      if (error) {
        console.error('Erro ao verificar se tópico está salvo:', error);
        return false;
      }
      return data as boolean || false;
    },

    getAll: async (): Promise<{ data: any[] | null; error: any }> => {
      try {
        // Get current user
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData.user?.id;
        
        if (!userId) {
          return { data: null, error: { message: 'Usuário não autenticado' } };
        }

        // Use the optimized function
        const { data, error } = await supabase.rpc('get_saved_topics_optimized', {
          user_uuid: userId
        });

        return { data: data as any[], error };
      } catch (error) {
        console.error('Erro inesperado ao buscar tópicos salvos:', error);
        return { data: null, error };
      }
    }
  },
  // Categorias
  getCategories: async (): Promise<{ data: ForumCategory[] | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('forum_categories')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) {
        console.error('Erro ao buscar categorias:', error);
        return { data: null, error };
      }

      return { data: data as ForumCategory[], error: null };
    } catch (error) {
      console.error('Erro inesperado ao buscar categorias:', error);
      return { data: null, error };
    }
  },

  createCategory: async (category: Omit<ForumCategory, 'id' | 'created_at' | 'updated_at'>): Promise<{ data: ForumCategory | null; error: any }> => {
    try {
      console.log('Tentando criar categoria:', category);
      
      // Usar função SQL personalizada para criar categoria
      const { data, error } = await supabase
        .rpc('insert_forum_category', {
          category_name: category.name,
          category_description: category.description || '',
          category_color: category.color,
          category_icon: category.icon || '',
          category_banner_url: category.banner_url || '',
          category_custom_tag: category.custom_tag || ''
        });

      if (error) {
        console.error('Erro ao criar categoria:', error);
        return { data: null, error };
      }

      console.log('Resultado da função SQL:', data);
      
      // A função SQL retorna o objeto da categoria criada diretamente
      if (data && data.length > 0) {
        console.log('Categoria criada com sucesso');
        return { data: data[0] as ForumCategory, error: null };
      } else {
        console.error('Erro retornado pela função SQL:', data);
        return { data: null, error: { message: 'Erro desconhecido' } };
      }
    } catch (error) {
      console.error('Erro inesperado ao criar categoria:', error);
      return { data: null, error };
    }
  },

  updateCategory: async (id: string, updates: Partial<ForumCategory>): Promise<{ data: ForumCategory | null; error: any }> => {
    try {
      console.log('Tentando atualizar categoria:', id, updates);
      
      // Usar função SQL personalizada para atualizar categoria
      const { data, error } = await supabase
        .rpc('update_forum_category', {
          category_id: id,
          category_name: updates.name || '',
          category_description: updates.description || '',
          category_color: updates.color || '',
          category_icon: updates.icon || '',
          category_banner_url: updates.banner_url || '',
          category_custom_tag: updates.custom_tag || ''
        });

      if (error) {
        console.error('Erro ao atualizar categoria:', error);
        return { data: null, error };
      }

      console.log('Resultado da função SQL:', data);
      
      // A função SQL retorna o objeto da categoria atualizada diretamente
      if (data && data.length > 0) {
        console.log('Categoria atualizada com sucesso');
        return { data: data[0] as ForumCategory, error: null };
      } else {
        console.error('Erro retornado pela função SQL:', data);
        return { data: null, error: { message: 'Erro desconhecido' } };
      }
    } catch (error) {
      console.error('Erro inesperado ao atualizar categoria:', error);
      return { data: null, error };
    }
  },

  getCommunityStats: async (): Promise<{ data: { total_users: number; total_topics: number; weekly_engagement: number } | null; error: any }> => {
    try {
      console.log('📊 Buscando estatísticas da comunidade...');
      
      const { data, error } = await supabase
        .rpc('get_community_stats_optimized');
      
      if (error) {
        console.error('❌ Erro ao buscar estatísticas:', error);
        return { data: null, error };
      }
      
      console.log('✅ Estatísticas obtidas:', data);
      return { data: data?.[0] as { total_users: number; total_topics: number; weekly_engagement: number } || null, error: null };
    } catch (error) {
      console.error('❌ Erro inesperado ao buscar estatísticas:', error);
      return { data: null, error };
    }
  },

  deleteCategory: async (id: string): Promise<{ success: boolean; error: any }> => {
    try {
      console.log('Tentando deletar categoria:', id);
      
      // Verificar status da autenticação
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      console.log('Status da autenticação:', { user: user?.id, email: user?.email, authError });
      
      if (authError) {
        console.error('Erro de autenticação:', authError);
        return { success: false, error: authError };
      }
      
      if (!user) {
        console.error('Usuário não autenticado');
        return { success: false, error: { message: 'Usuário não autenticado' } };
      }
      
      // Usar função SQL personalizada para deletar categoria
      const { data, error } = await supabase
        .rpc('delete_forum_category', { category_id: id });

      if (error) {
        console.error('Erro ao deletar categoria:', error);
        return { success: false, error };
      }

      console.log('Resultado da função SQL:', data);
      
      if (data && data.success) {
        console.log('Categoria deletada com sucesso (soft delete)');
        return { success: true, error: null };
      } else {
        console.error('Erro retornado pela função SQL:', data);
        return { success: false, error: { message: data?.message || 'Erro desconhecido' } };
      }
    } catch (error) {
      console.error('Erro inesperado ao deletar categoria:', error);
      return { success: false, error };
    }
  },

  // Upload de imagens para categorias
  uploadCategoryBanner: async (categoryId: string, file: File): Promise<{ url: string | null; error: any }> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${categoryId}.${fileExt}`;
      const filePath = `category-banners/${fileName}`;

      const { data, error } = await supabase.storage
        .from('banners')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error('Erro ao fazer upload da imagem:', error);
        return { url: null, error };
      }

      // Obter URL pública da imagem
      const { data: { publicUrl } } = supabase.storage
        .from('banners')
        .getPublicUrl(filePath);

      return { url: publicUrl, error: null };
    } catch (error) {
      console.error('Erro inesperado ao fazer upload:', error);
      return { url: null, error };
    }
  },

  // Deletar imagem de categoria
  deleteCategoryBanner: async (categoryId: string): Promise<{ success: boolean; error: any }> => {
    try {
      const { error } = await supabase.storage
        .from('banners')
        .remove([`category-banners/${categoryId}.png`]);

      if (error) {
        console.error('Erro ao deletar imagem:', error);
        return { success: false, error };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Erro inesperado ao deletar imagem:', error);
      return { success: false, error };
    }
  },

  // Tópicos
  getTopics: async (categoryId?: string): Promise<{ data: ForumTopic[] | null; error: any }> => {
    try {
      let query = supabase
        .from('forum_topics')
        .select(`
          *,
          category:forum_categories(*),
          author:user_profiles(id, full_name, email, avatar_url)
        `)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar tópicos:', error);
        return { data: null, error };
      }

      return { data: data as ForumTopic[], error: null };
    } catch (error) {
      console.error('Erro inesperado ao buscar tópicos:', error);
      return { data: null, error };
    }
  },

  getTopic: async (id: string): Promise<{ data: ForumTopic | null; error: any }> => {
    try {
      // Incrementar contador de visualizações
      await supabase.rpc('increment_topic_view_count', { topic_uuid: id });

      const { data, error } = await supabase
        .from('forum_topics')
        .select(`
          *,
          category:forum_categories(*),
          author:user_profiles(id, full_name, email, avatar_url)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Erro ao buscar tópico:', error);
        return { data: null, error };
      }

      console.log('📊 Tópico carregado com votes_count:', data?.votes_count);
      return { data: data as ForumTopic, error: null };
    } catch (error) {
      console.error('Erro inesperado ao buscar tópico:', error);
      return { data: null, error };
    }
  },

  createTopic: async (topic: Omit<ForumTopic, 'id' | 'created_at' | 'updated_at' | 'view_count' | 'reply_count'>): Promise<{ data: ForumTopic | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('forum_topics')
        .insert([topic])
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar tópico:', error);
        return { data: null, error };
      }

      return { data: data as ForumTopic, error: null };
    } catch (error) {
      console.error('Erro inesperado ao criar tópico:', error);
      return { data: null, error };
    }
  },

  updateTopic: async (id: string, updates: Partial<ForumTopic>): Promise<{ data: ForumTopic | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('forum_topics')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar tópico:', error);
        return { data: null, error };
      }

      return { data: data as ForumTopic, error: null };
    } catch (error) {
      console.error('Erro inesperado ao atualizar tópico:', error);
      return { data: null, error };
    }
  },

  deleteTopic: async (id: string): Promise<{ success: boolean; error: any }> => {
    try {
      const { error } = await supabase
        .from('forum_topics')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao deletar tópico:', error);
        return { success: false, error };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Erro inesperado ao deletar tópico:', error);
      return { success: false, error };
    }
  },

  // Respostas
  getReplies: async (topicId: string): Promise<{ data: ForumReply[] | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('forum_replies')
        .select(`
          *,
          author:user_profiles(id, full_name, email, avatar_url)
        `)
        .eq('topic_id', topicId)
        .is('parent_reply_id', null) // Apenas respostas principais
        .order('is_solution', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Erro ao buscar respostas:', error);
        return { data: null, error };
      }

      return { data: data as ForumReply[], error: null };
    } catch (error) {
      console.error('Erro inesperado ao buscar respostas:', error);
      return { data: null, error };
    }
  },

  getRepliesByParent: async (parentReplyId: string): Promise<{ data: ForumReply[] | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('forum_replies')
        .select(`
          *,
          author:user_profiles(id, full_name, email, avatar_url)
        `)
        .eq('parent_reply_id', parentReplyId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Erro ao buscar respostas aninhadas:', error);
        return { data: null, error };
      }

      return { data: data as ForumReply[], error: null };
    } catch (error) {
      console.error('Erro inesperado ao buscar respostas aninhadas:', error);
      return { data: null, error };
    }
  },

  createReply: async (reply: Omit<ForumReply, 'id' | 'created_at' | 'updated_at'>): Promise<{ data: ForumReply | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('forum_replies')
        .insert([reply])
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar resposta:', error);
        return { data: null, error };
      }

      return { data: data as ForumReply, error: null };
    } catch (error) {
      console.error('Erro inesperado ao criar resposta:', error);
      return { data: null, error };
    }
  },

  updateReply: async (id: string, updates: Partial<ForumReply>): Promise<{ data: ForumReply | null; error: any }> => {
    try {
      const { data, error } = await supabase
        .from('forum_replies')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar resposta:', error);
        return { data: null, error };
      }

      return { data: data as ForumReply, error: null };
    } catch (error) {
      console.error('Erro inesperado ao atualizar resposta:', error);
      return { data: null, error };
    }
  },

  deleteReply: async (id: string): Promise<{ success: boolean; error: any }> => {
    try {
      const { error } = await supabase
        .from('forum_replies')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erro ao deletar resposta:', error);
        return { success: false, error };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Erro inesperado ao deletar resposta:', error);
      return { success: false, error };
    }
  },

  // Likes e Curtidas
  getLikesCount: async (topicId?: string, replyId?: string): Promise<{ count: number; error: any }> => {
    try {
      let query;
      if (topicId) {
        query = supabase.rpc('get_topic_likes_count', { topic_uuid: topicId });
      } else if (replyId) {
        query = supabase.rpc('get_reply_likes_count', { reply_uuid: replyId });
      } else {
        return { count: 0, error: { message: 'É necessário fornecer topicId ou replyId' } };
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar contagem de likes:', error);
        return { count: 0, error };
      }

      return { count: data || 0, error: null };
    } catch (error) {
      console.error('Erro inesperado ao buscar contagem de likes:', error);
      return { count: 0, error };
    }
  },

  hasUserLiked: async (topicId?: string, replyId?: string): Promise<{ hasLiked: boolean; error: any }> => {
    try {
      let query;
      if (topicId) {
        query = supabase.rpc('has_user_liked_topic', { topic_uuid: topicId });
      } else if (replyId) {
        query = supabase.rpc('has_user_liked_reply', { reply_uuid: replyId });
      } else {
        return { hasLiked: false, error: { message: 'É necessário fornecer topicId ou replyId' } };
      }

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao verificar like do usuário:', error);
        return { hasLiked: false, error };
      }

      return { hasLiked: data || false, error: null };
    } catch (error) {
      console.error('Erro inesperado ao verificar like do usuário:', error);
      return { hasLiked: false, error };
    }
  },

  toggleTopicLike: async (topicId: string): Promise<{ success: boolean; likes: number; error: any }> => {
    try {
      const { data, error } = await supabase.rpc('toggle_topic_like', { topic_uuid: topicId });

      if (error) {
        console.error('Erro ao dar/remover like do tópico:', error);
        return { success: false, likes: 0, error };
      }

      const result = data as any;
      return { 
        success: result.success, 
        likes: result.likes || 0, 
        error: null 
      };
    } catch (error) {
      console.error('Erro inesperado ao dar/remover like do tópico:', error);
      return { success: false, likes: 0, error };
    }
  },

  toggleReplyLike: async (replyId: string): Promise<{ success: boolean; likes: number; error: any }> => {
    try {
      const { data, error } = await supabase.rpc('toggle_reply_like', { reply_uuid: replyId });

      if (error) {
        console.error('Erro ao dar/remover like da resposta:', error);
        return { success: false, likes: 0, error };
      }

      const result = data as any;
      return { 
        success: result.success, 
        likes: result.likes || 0, 
        error: null 
      };
    } catch (error) {
      console.error('Erro inesperado ao dar/remover like da resposta:', error);
      return { success: false, likes: 0, error };
    }
  },

  // Funções para Links Úteis
  usefulLinks: {
    // Buscar todos os links úteis ativos
    getAll: async (): Promise<{ data: UsefulLink[]; error: any }> => {
      try {
        const { data, error } = await supabase
          .from('useful_links')
          .select('*')
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Erro ao buscar links úteis:', error);
          return { data: [], error };
        }

        return { data: data || [], error: null };
      } catch (error) {
        console.error('Erro inesperado ao buscar links úteis:', error);
        return { data: [], error };
      }
    },

    // Criar novo link útil
    create: async (linkData: Omit<UsefulLink, 'id' | 'created_at' | 'updated_at'>): Promise<{ data: UsefulLink | null; error: any }> => {
      try {
        const { data, error } = await supabase
          .from('useful_links')
          .insert([linkData])
          .select()
          .single();

        if (error) {
          console.error('Erro ao criar link útil:', error);
          return { data: null, error };
        }

        return { data, error: null };
      } catch (error) {
        console.error('Erro inesperado ao criar link útil:', error);
        return { data: null, error };
      }
    },

    // Atualizar link útil
    update: async (id: string, linkData: Partial<Omit<UsefulLink, 'id' | 'created_at' | 'updated_at'>>): Promise<{ data: UsefulLink | null; error: any }> => {
      try {
        const { data, error } = await supabase
          .from('useful_links')
          .update({
            ...linkData,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)
          .select()
          .single();

        if (error) {
          console.error('Erro ao atualizar link útil:', error);
          return { data: null, error };
        }

        return { data, error: null };
      } catch (error) {
        console.error('Erro inesperado ao atualizar link útil:', error);
        return { data: null, error };
      }
    },

    // Deletar link útil (soft delete)
    delete: async (id: string): Promise<{ success: boolean; error: any }> => {
      try {
        const { error } = await supabase
          .from('useful_links')
          .update({ 
            is_active: false,
            updated_at: new Date().toISOString()
          })
          .eq('id', id);

        if (error) {
          console.error('Erro ao deletar link útil:', error);
          return { success: false, error };
        }

        return { success: true, error: null };
      } catch (error) {
        console.error('Erro inesperado ao deletar link útil:', error);
        return { success: false, error };
      }
    },

    // Inscrever para mudanças em tempo real
    subscribeToChanges: (callback: (payload: any) => void) => {
      console.log('🔔 Iniciando subscription para useful_links');
      
      const channel = supabase
        .channel('useful_links_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'useful_links'
          },
          (payload) => {
            console.log('📡 Evento real-time recebido:', payload);
            callback(payload);
          }
        )
        .on('presence', { event: 'sync' }, () => {
          console.log('👥 Presence sync');
        })
        .on('presence', { event: 'join' }, ({ key, newPresences }) => {
          console.log('👋 Presence join:', key, newPresences);
        })
        .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
          console.log('👋 Presence leave:', key, leftPresences);
        })
        .subscribe((status) => {
          console.log('📡 Status da subscription:', status);
        });
      
      return channel;
    }
  },


};

// Exportar as funções de links úteis
export const usefulLinksHelpers = {
  getAll: async (): Promise<{ data: UsefulLink[]; error: any }> => {
    try {
      console.log('🔄 Carregando links úteis...');
      
      const { data, error } = await supabase
        .from('useful_links')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Erro ao buscar links úteis:', error);
        return { data: [], error };
      }

      console.log('✅ Links carregados com sucesso:', data?.length || 0, 'links');
      return { data: data || [], error: null };
    } catch (error) {
      console.error('❌ Erro inesperado ao buscar links úteis:', error);
      return { data: [], error };
    }
  },

  create: async (linkData: Omit<UsefulLink, 'id' | 'created_at' | 'updated_at'>): Promise<{ data: UsefulLink | null; error: any }> => {
    try {
      // Verificar autenticação primeiro
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('Erro de autenticação:', authError);
        return { data: null, error: { message: 'Usuário não autenticado' } };
      }

      console.log('Usuário autenticado:', user.email, 'ID:', user.id);

      const { data, error } = await supabase
        .from('useful_links')
        .insert([linkData])
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar link útil:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Erro inesperado ao criar link útil:', error);
      return { data: null, error };
    }
  },

  update: async (id: string, linkData: Partial<Omit<UsefulLink, 'id' | 'created_at' | 'updated_at'>>): Promise<{ data: UsefulLink | null; error: any }> => {
    try {
      // Verificar autenticação primeiro
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('Erro de autenticação:', authError);
        return { data: null, error: { message: 'Usuário não autenticado' } };
      }

      console.log('Usuário autenticado:', user.email, 'ID:', user.id);

      const { data, error } = await supabase
        .from('useful_links')
        .update({
          ...linkData,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar link útil:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Erro inesperado ao atualizar link útil:', error);
      return { data: null, error };
    }
  },

  delete: async (id: string): Promise<{ success: boolean; error: any }> => {
    try {
      console.log('🔍 Iniciando delete para ID:', id);
      console.log('🔍 Tipo do ID:', typeof id);
      console.log('🔍 ID válido:', id && id.length > 0);
      
      // Verificar autenticação primeiro
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('❌ Erro de autenticação:', authError);
        console.error('❌ Usuário:', user);
        return { success: false, error: { message: 'Usuário não autenticado' } };
      }

      console.log('✅ Usuário autenticado:', user.email, 'ID:', user.id);

      // Verificar se o link existe antes de tentar deletar
      console.log('🔍 Verificando se o link existe...');
      const { data: existingLink, error: checkError } = await supabase
        .from('useful_links')
        .select('*')
        .eq('id', id)
        .single();

      if (checkError) {
        console.error('❌ Erro ao verificar se link existe:', checkError);
        return { success: false, error: { message: 'Link não encontrado' } };
      }

      if (!existingLink) {
        console.error('❌ Link não encontrado no banco de dados');
        return { success: false, error: { message: 'Link não encontrado' } };
      }

      console.log('✅ Link encontrado:', existingLink.title);

      // Tentar usar função RPC para soft delete (contorna RLS)
      console.log('🔄 Executando soft delete via RPC...');
      const { data, error: rpcError } = await supabase.rpc('delete_useful_link', {
        link_id: id
      });

      if (rpcError) {
        console.warn('⚠️ RPC delete_useful_link não disponível, tentando método direto...');
        
        // Fallback: tentar UPDATE direto (pode falhar devido ao RLS)
        const { data: updateData, error } = await supabase
          .from('useful_links')
          .update({ 
            is_active: false,
            updated_at: new Date().toISOString()
          })
          .eq('id', id)
          .select();

        console.log('📊 Resultado do update (fallback):', { error, data: updateData });

        if (error) {
          console.error('❌ Erro ao deletar link útil:', error);
          console.error('❌ Código do erro:', error.code);
          console.error('❌ Mensagem do erro:', error.message);
          console.error('❌ Detalhes do erro:', error.details);
          
          // Tentar abordagem alternativa: DELETE real em vez de soft delete
          console.log('🔄 Tentando DELETE real como última alternativa...');
          const { error: deleteError } = await supabase
            .from('useful_links')
            .delete()
            .eq('id', id);

          if (deleteError) {
            console.error('❌ Erro no DELETE real:', deleteError);
            return { success: false, error: deleteError };
          }

          console.log('✅ Link deletado com sucesso (DELETE real)');
          return { success: true, error: null };
        }

        console.log('✅ Link deletado com sucesso (soft delete - fallback)');
        console.log('✅ Dados atualizados:', updateData);
        return { success: true, error: null };
      } else if (data && !data.success) {
        console.error('❌ Erro na exclusão (RPC):', data.message);
        return { success: false, error: { message: data.message } };
      }

      console.log('✅ Link deletado com sucesso (soft delete - RPC)');
      return { success: true, error: null };
    } catch (error) {
      console.error('❌ Erro inesperado ao deletar link útil:', error);
      console.error('❌ Tipo do erro:', typeof error);
      console.error('❌ Stack trace:', error instanceof Error ? error.stack : 'N/A');
      return { success: false, error };
    }
  },

  subscribeToChanges: (callback: (payload: any) => void) => {
    return supabase
      .channel('useful_links_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'useful_links'
        },
        callback
      )
      .subscribe();
  },

  // Debug function to check authentication and user profile
  debugAuth: async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('❌ Auth error:', authError);
        return { authenticated: false, user: null, profile: null };
      }

      console.log('✅ User authenticated:', user.email, 'ID:', user.id);

      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('❌ Profile error:', profileError);
        return { authenticated: true, user, profile: null };
      }

      console.log('✅ Profile found:', profile);
      return { authenticated: true, user, profile };
    } catch (error) {
      console.error('❌ Debug auth error:', error);
      return { authenticated: false, user: null, profile: null };
    }
  },

  // Debug function to check database permissions and RLS policies
  debugDatabasePermissions: async () => {
    try {
      console.log('🔍 Verificando permissões do banco de dados...');
      
      // Testar SELECT
      const { data: selectTest, error: selectError } = await supabase
        .from('useful_links')
        .select('count')
        .limit(1);
      
      console.log('📊 Teste SELECT:', { success: !selectError, error: selectError });
      
      // Testar INSERT (apenas para verificar permissões)
      const { data: insertTest, error: insertError } = await supabase
        .from('useful_links')
        .insert([{
          title: 'TEST_LINK',
          description: 'Test link for permissions',
          url: 'https://test.com',
          image: 'https://test.com/image.jpg',
          created_by: 'test-user',
          is_active: false
        }])
        .select();
      
      console.log('📊 Teste INSERT:', { success: !insertError, error: insertError });
      
      // Se o INSERT funcionou, deletar o teste
      if (insertTest && insertTest.length > 0) {
        const { error: deleteTestError } = await supabase
          .from('useful_links')
          .delete()
          .eq('title', 'TEST_LINK');
        
        console.log('📊 Limpeza do teste:', { success: !deleteTestError, error: deleteTestError });
      }
      
      return {
        select: { success: !selectError, error: selectError },
        insert: { success: !insertError, error: insertError }
      };
    } catch (error) {
      console.error('❌ Erro ao verificar permissões:', error);
      return { error };
    }
  },

  // Funções para gerenciar etiquetas
  getTags: async (): Promise<{ data: ForumTag[] | null; error: any }> => {
    try {
      console.log('🏷️ Buscando etiquetas...');
      const { data, error } = await supabase.rpc('get_forum_tags');
      
      if (error) {
        console.error('❌ Erro ao buscar etiquetas:', error);
        return { data: null, error };
      }
      
      console.log('✅ Etiquetas obtidas:', data);
      return { data: data || [], error: null };
    } catch (error) {
      console.error('❌ Erro inesperado ao buscar etiquetas:', error);
      return { data: null, error };
    }
  },

  createTag: async (name: string, color: string = '#0FFF55'): Promise<{ data: ForumTag | null; error: any }> => {
    try {
      console.log('🏷️ Criando etiqueta:', { name, color });
      const { data, error } = await supabase.rpc('insert_forum_tag', {
        tag_name: name,
        tag_color: color
      });
      
      if (error) {
        console.error('❌ Erro ao criar etiqueta:', error);
        return { data: null, error };
      }
      
      console.log('✅ Etiqueta criada:', data);
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      console.error('❌ Erro inesperado ao criar etiqueta:', error);
      return { data: null, error };
    }
  },

  updateTag: async (id: string, name: string, color: string): Promise<{ data: ForumTag | null; error: any }> => {
    try {
      console.log('🏷️ Atualizando etiqueta:', { id, name, color });
      const { data, error } = await supabase.rpc('update_forum_tag', {
        tag_id: id,
        tag_name: name,
        tag_color: color
      });
      
      if (error) {
        console.error('❌ Erro ao atualizar etiqueta:', error);
        return { data: null, error };
      }
      
      console.log('✅ Etiqueta atualizada:', data);
      return { data: data?.[0] || null, error: null };
    } catch (error) {
      console.error('❌ Erro inesperado ao atualizar etiqueta:', error);
      return { data: null, error };
    }
  },

  deleteTag: async (id: string): Promise<{ success: boolean; error: any }> => {
    try {
      console.log('🏷️ Deletando etiqueta:', id);
      const { data, error } = await supabase.rpc('delete_forum_tag', {
        tag_id: id
      });
      
      if (error) {
        console.error('❌ Erro ao deletar etiqueta:', error);
        return { success: false, error };
      }
      
      console.log('✅ Etiqueta deletada:', data);
      return { success: true, error: null };
    } catch (error) {
      console.error('❌ Erro inesperado ao deletar etiqueta:', error);
      return { success: false, error };
    }
  }
};

// Funções para upload de arquivos
export const storageHelpers = {
  // Upload de imagem de perfil com compressão
  uploadProfileImage: async (file: File, userId: string): Promise<{ url: string; error?: string }> => {
    try {
      // Comprimir a imagem antes do upload
      const compressedFile = await compressImage(file, {
        maxWidth: 400,
        maxHeight: 400,
        quality: 0.8,
        format: 'webp'
      });

      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/profile.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, compressedFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error('Erro ao fazer upload da imagem de perfil:', error);
        return { url: '', error: error.message };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      return { url: publicUrl };
    } catch (err) {
      console.error('Erro inesperado no upload da imagem de perfil:', err);
      return { url: '', error: 'Erro inesperado no upload' };
    }
  },

  // Upload de mídia para discussões
  uploadDiscussionMedia: async (file: File, topicId: string, replyId?: string): Promise<{ url: string; error?: string }> => {
    try {
      let compressedFile = file;
      const fileType = file.type.split('/')[0];

      // Comprimir apenas imagens
      if (fileType === 'image') {
        compressedFile = await compressImage(file, {
          maxWidth: 1200,
          maxHeight: 1200,
          quality: 0.85,
          format: 'webp'
        });
      }

      const fileExt = file.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = replyId 
        ? `${topicId}/replies/${replyId}/${timestamp}.${fileExt}`
        : `${topicId}/topic/${timestamp}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('discussion-media')
        .upload(fileName, compressedFile, {
          cacheControl: '3600'
        });

      if (error) {
        console.error('Erro ao fazer upload da mídia:', error);
        return { url: '', error: error.message };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('discussion-media')
        .getPublicUrl(fileName);

      return { url: publicUrl };
    } catch (err) {
      console.error('Erro inesperado no upload da mídia:', err);
      return { url: '', error: 'Erro inesperado no upload' };
    }
  },

  // Upload genérico com compressão automática, criação dinâmica de bucket e RLS/Quota control
  uploadGenericFile: async (
    bucketName: string, 
    file: File, 
    options?: { maxWidth?: number; maxHeight?: number; quality?: number; format?: string }
  ): Promise<{ url: string; error?: string }> => {
    try {
      let finalFile = file;
      const fileType = file.type.split('/')[0];

      // Comprimir apenas imagens
      if (fileType === 'image') {
        const compressOpts = {
          maxWidth: options?.maxWidth || 1200,
          maxHeight: options?.maxHeight || 1200,
          quality: options?.quality || 0.85,
          format: options?.format || 'webp'
        };
        finalFile = await compressImage(file, compressOpts);
      }

      // Gerar nome limpo e seguro
      const fileExt = options?.format || 'webp';
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      const cleanFileName = `${timestamp}_${randomString}.${fileExt}`;

      // Upload do arquivo
      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(cleanFileName, finalFile, {
          cacheControl: '3600'
        });

      if (error) {
        console.error(`Erro no upload para o bucket ${bucketName}:`, error);

        // Se o bucket não existir, tentar criar automaticamente
        if (error.message?.includes('not found') || (error as any).status === 404) {
          console.log(`Tentando criar o bucket ${bucketName} dinamicamente...`);
          const { error: createBucketError } = await supabase.storage.createBucket(bucketName, {
            public: true
          });

          if (!createBucketError) {
            // Tentar novamente o upload
            const { data: retryData, error: retryError } = await supabase.storage
              .from(bucketName)
              .upload(cleanFileName, finalFile, {
                cacheControl: '3600'
              });

            if (!retryError) {
              const { data: { publicUrl } } = supabase.storage
                .from(bucketName)
                .getPublicUrl(cleanFileName);
              return { url: publicUrl };
            } else {
              return { url: '', error: `Erro no upload após criar o bucket: ${retryError.message}` };
            }
          } else {
            return { url: '', error: `Erro ao criar bucket automático: ${createBucketError.message}` };
          }
        }

        // Se for erro de quota excedida
        if (error.message?.toLowerCase().includes('quota') || 
            error.message?.toLowerCase().includes('limit') || 
            (error as any).status === 413) {
          return { url: '', error: 'ARMAZENAMENTO_CHEIO' };
        }

        return { url: '', error: error.message };
      }

      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(cleanFileName);

      return { url: publicUrl };
    } catch (err: any) {
      console.error('Erro inesperado no upload genérico:', err);
      return { url: '', error: err.message || 'Erro inesperado' };
    }
  },

  // Deletar arquivo
  deleteFile: async (url: string, bucket: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const path = url.split('/').pop();
      if (!path) {
        return { success: false, error: 'URL inválida' };
      }

      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) {
        console.error('Erro ao deletar arquivo:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      console.error('Erro inesperado ao deletar arquivo:', err);
      return { success: false, error: 'Erro inesperado' };
    }
  }
};

// Função para comprimir imagens
const compressImage = async (
  file: File, 
  options: { maxWidth: number; maxHeight: number; quality: number; format: string }
): Promise<File> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();

    img.onload = () => {
      // Calcular novas dimensões mantendo proporção
      let { width, height } = img;
      const { maxWidth, maxHeight } = options;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      canvas.width = width;
      canvas.height = height;

      // Desenhar imagem redimensionada
      ctx.drawImage(img, 0, 0, width, height);

      // Converter para blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: `image/${options.format}`,
              lastModified: Date.now()
            });
            resolve(compressedFile);
          } else {
            resolve(file);
          }
        },
        `image/${options.format}`,
        options.quality
      );
    };

    img.src = URL.createObjectURL(file);
  });
};